import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { DownloadFile, DownloadFileService } from '../../../services/file-downloader/download-file.service';
import { SalespersonBudget, SalespersonBudgetImportResult } from '../models/salesperson-budget';

@Injectable({
  providedIn: 'root'
})
export class SalespersonBudgetService {
  protected http = inject(HttpClient);
  protected downloadFileService = inject(DownloadFileService);

  protected baseUrl = 'json/salesperson-budgets';

  getByYear(year: number): Observable<SalespersonBudget[]> {
    const headers = { maxAge: '-1' };
    return this.http.get<SalespersonBudget[]>(`${this.baseUrl}/by-year/${year}`, { headers });
  }

  getById(id: string): Observable<SalespersonBudget> {
    const headers = { maxAge: '-1' };
    return this.http.get<SalespersonBudget>(`${this.baseUrl}/${id}`, { headers });
  }

  getByYearAndSalesperson(salespersonId: string, year: number): Observable<SalespersonBudget> {
    const headers = { maxAge: '-1' };
    return this.http.get<SalespersonBudget>(
      `${this.baseUrl}/by-salesperson/${salespersonId}/year/${year}`,
      { headers }
    );
  }

  save(budget: SalespersonBudget): Observable<SalespersonBudget> {
    const body: SalespersonBudget = { ...budget };
    // Backend upserts on (salespersonId, year); omit id to avoid conflicts on create.
    delete body.id;
    return this.http.post<SalespersonBudget>(this.baseUrl, body);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  exportXlsx(year: number): Observable<any> {
    const url = `${this.baseUrl}/xlsx/${year}`;
    return this.http.get(url, { responseType: 'arraybuffer', observe: 'response' }).pipe(
      map((response: HttpResponse<ArrayBuffer>) => this.downloadFileService.create(response)),
      map((file: DownloadFile) => this.downloadFileService.download(file))
    );
  }

  importXlsx(year: number, file: File): Observable<SalespersonBudgetImportResult> {
    const url = `${this.baseUrl}/xlsx/import?year=${year}`;
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<SalespersonBudgetImportResult>(url, formData);
  }
}
